/* ==========================================================================
   CEO 2026 (built on the forked theme) · ce-fx.js · the effects kit
   "The blend never stops": the boards' three patterns kept quietly alive and, in the footer only,
   deep water that a small blender dial can turn.

   No dependencies. Load once, deferred (a second load does nothing):
     <script src="{{ 'ce-fx.js' | asset_url }}" defer="defer"></script>
   Looks, static fallbacks and the dial's styles: ce-fx.css. How a section uses each piece: docs/FX.md.

     [data-ce-fx="lines"]          vortex lines: a swirl of fine streaks, three rings turning slowly
     [data-ce-fx="wave"]           liquid wave edge of a section (CSS moves it; this pauses it off-screen)
     [data-ce-fx="dots"]           ingredient dot grid that drifts a little with the scroll
     [data-ce-fx="water"]          the water: a calm WebGL whirlpool (footer)
     <ce-dial for="water id">      the 1-10 blender dial around its content; it drives the water
     [data-ce-pulse]               a button: a burst of full speed while held ("PULSE")
     [data-ce-speed-readout]       mirrors the water's speed number
     [data-ce-pulse-target="#id"]  any button: hovering or pressing it spins those lines up
     [data-ce-fx="rise"]           a one-shot "rise and settle" entrance

   window.CEFx.get(el)   lines -> { spinUp(multiplier, ms), hold(on), pause(), resume() }
                         water -> { speed, setSpeed(n), pulse(ms), pause(), resume() }
   window.CEFx.init(root) binds new markup (done for you on load and on shopify:section:load).
   Events (they bubble): "ce:fx:speed" from the water, "ce:dial:change" from a dial.
   ?bofxdebug=1 shows frame times and the water's resolution scale (noadapt = the same, quality fixed;
   slow and slower play a weak graphics chip, to test the adaptive quality).
   ========================================================================== */
(() => {
  'use strict';
  const W = window, D = document, H = D.documentElement;
  if (W.CEFx || !('IntersectionObserver' in W && 'ResizeObserver' in W && W.customElements)) return;   // old browser: the CSS looks stay

  const reduce = W.matchMedia('(prefers-reduced-motion: reduce)');
  const phone = W.matchMedia('(max-width: 749px)');
  const debug = (/[?&]bofxdebug=(\w+)/.exec(W.location.search) || [])[1];
  const TAU = Math.PI * 2;
  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
  const ease = (from, to, dt, rate) => from + (to - from) * (1 - Math.exp(-dt * rate));   // exponential ease toward a target
  const idle = W.requestIdleCallback ? (f) => W.requestIdleCallback(f, { timeout: 500 }) : (f) => setTimeout(f, 60);
  const fire = (el, name, detail) => el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  const each = (root, sel, fn) => { if (root.matches && root.matches(sel)) fn(root); root.querySelectorAll(sel).forEach(fn); };
  // data-ce-centre="18% 50%" -> [.18, .5]; phones may have their own in data-ce-centre-sm
  const centre = (d, x, y) => { const m = ((phone.matches && d.ceCentreSm) || d.ceCentre || '').match(/-?[\d.]+/g); return m && m.length > 1 ? [m[0] / 100, m[1] / 100] : [x, y]; };
  let visit = (Date.now() % 2147483646) + 1;                // a different picture on every visit
  const dice = () => (visit = visit * 48271 % 2147483647);

  /* ---------- one frame loop for everything that needs JavaScript frames ---------- */
  const busy = new Set(), all = new Set(), byEl = new WeakMap();
  const stat = { ms: 16.7, draw: 0, scale: 1, drops: 0, state: 'live', lines: 0, compile: 0 };
  let raf = 0, then = 0;
  const frame = (t) => {
    const ms = t - then, dt = Math.min(ms / 1000, .05);
    then = t;
    stat.ms += (Math.min(ms, 100) - stat.ms) * .06;
    busy.forEach((o) => o.tick(t, dt));
    raf = busy.size ? requestAnimationFrame(frame) : 0;     // (raf stays set during the ticks, so a wake() in there cannot start a second loop)
  };
  const wake = (o) => { busy.add(o); if (!raf) { then = performance.now(); raf = requestAnimationFrame(frame); } };
  const rest = (o) => busy.delete(o);

  // seen: on screen (run) · near: within a screen of it (hold the pictures in memory) · sized: the box changed
  const watch = (margin, key, hook) => new IntersectionObserver((ens) => ens.forEach((en) => { const o = byEl.get(en.target); if (o) { o[key] = en.isIntersecting; o[hook](); } }), { rootMargin: margin });
  const seen = watch('60px 0px', 'seen', '_sync'), near = watch('100% 0px', 'near', '_fit');
  // fix-speed 02-10: an effect is measured here, right after the browser's own layout, where a read costs nothing.
  // A read in a later task, or after a write, made a phone lay the whole page out again (1.5-1.7 s of the home
  // page's start at a phone's speed). Every read first, then every write; the drawing still waits 120 ms for a
  // size to settle. remeasure(): observed again = measured again, for free, after the next layout.
  const sized = new ResizeObserver((ens) => {
    const due = ens.map((en) => byEl.get(en.target)).filter(Boolean);
    due.forEach((o) => o._measure());
    due.forEach((o) => { clearTimeout(o.rz); o.rz = setTimeout(() => o._size(true), o.geo ? 120 : 0); });
  });
  const remeasure = (o) => { sized.unobserve(o.el); sized.observe(o.el); };
  const track = (o) => { all.add(o); byEl.set(o.el, o); o.el.setAttribute('aria-hidden', 'true'); [seen, near, sized].forEach((w) => w.observe(o.el)); };
  const untrack = (o) => { rest(o); all.delete(o); byEl.delete(o.el); [seen, near, sized].forEach((w) => w.unobserve(o.el)); };

  /* ------------------------------------------------------------------
     Swirl lines. Every streak is a tapered arc of a logarithmic spiral: hairlines, a few broader
     strokes and some pale ribbons, gathered loosely into three arms, as on the owner's boards. They
     are drawn once on three canvases (inner, middle, outer ring); the rings then turn on the
     compositor at their own speed, the inner one fastest. No JavaScript runs per frame except while
     a button spins them up. Far from the screen the canvases give their memory back.
     ------------------------------------------------------------------ */
  const RINGS = [[.08, .42, 64, 46], [.3, .72, 100, 78], [.58, 1, 130, 128]];        // band (fractions of the radius) · streaks · seconds per turn
  const ARMS = 3, PITCH = .3, PITCH_VAR = .16;                                       // the swirl: arms, and how fast a streak falls toward the eye
  const INKS = {                                                                     // strength, then the colours
    'blue-on-light': [.82, '#0A74C6', '#3FA4EC', '#8CCBF5', '#00C6D7', '#B5DFFA', '#0A74C6', '#5DB3F0'],
    'light-on-blue': [.74, '#FFFFFF', '#CFE9FB', '#9FD6F8', '#FFFFFF', '#7FE3EC', '#CFE9FB'],
    'light-on-navy': [.6, '#3FA4EC', '#8CCBF5', '#0A74C6', '#00C6D7', '#5DB3F0', '#CFE9FB']
  };
  const streak = (ctx, c, r0, a0, span, pitch, w, taper) => {
    const n = clamp(Math.round(span * r0 / 14), 12, 96), up = [], down = [];
    for (let i = 0; i <= n; i++) {
      const t = i / n, a = a0 + span * t, r = r0 * Math.exp(-pitch * span * t), hw = .5 * w * Math.pow(Math.sin(Math.PI * t), taper), x = Math.cos(a), y = Math.sin(a);
      up.push(c + x * (r + hw), c + y * (r + hw));
      down.push(c + x * (r - hw), c + y * (r - hw));
    }
    ctx.beginPath();
    ctx.moveTo(up[0], up[1]);
    for (let i = 2; i < up.length; i += 2) ctx.lineTo(up[i], up[i + 1]);
    for (let i = down.length - 2; i >= 0; i -= 2) ctx.lineTo(down[i], down[i + 1]);
    ctx.fill();
  };

  class Lines {
    constructor(el) {
      this.el = el; this.rate = 1; this.burst = 1; this.until = 0;
      this.seed = +el.dataset.ceSeed || dice();
      this.cv = RINGS.map(() => el.appendChild(D.createElement('canvas')));
      track(this);
    }
    spinUp(multiplier, ms) { this.burst = Math.max(1, +multiplier || 9); this.until = performance.now() + (ms > 0 ? ms : 900); if (this.an) wake(this); }
    hold(on) { this.held = !!on; if (this.an) wake(this); }     // the pulse button: nine times speed while hovered or focused
    pause() { this.paused = true; this._sync(); }
    resume() { this.paused = false; this._sync(); }

    _measure() {                                    // reads only (see sized): the box, and where a pinned eye's element sits in it
      const el = this.el, m = this.m = { w: el.clientWidth, h: el.clientHeight, pin: null };
      // data-ce-centre-el="<selector>": the eye sits on that element of the same band while it is shown (Tommy 01-10:
      // the logo's blender in the eye of the finder's swirl). Measured on every resize, so it follows the layout.
      const pin = el.dataset.ceCentreEl && el.parentElement && el.parentElement.querySelector(el.dataset.ceCentreEl);
      if (pin && pin.offsetParent) {
        const hr = el.getBoundingClientRect(), pr = pin.getBoundingClientRect();
        if (pr.width > 0) m.pin = [pr.left + pr.width / 2 - hr.left, pr.top + pr.height / 2 - hr.top];
      }
    }
    _size(measured) {                               // place the eye; ask for a (re)draw when the radius changes
      if (!measured || !this.m) this._measure();
      const el = this.el, { w, h, pin } = this.m;
      if (w < 2 || h < 2) return;
      const c = centre(el.dataset, .5, .5);
      let x = w * c[0], y = h * c[1];
      if (pin) [x, y] = pin;
      const R = clamp(Math.hypot(Math.max(x, w - x), Math.max(y, h - y)) * .96, 300, 900);
      el.style.setProperty('--ce-fx-x', x.toFixed(1) + 'px');
      el.style.setProperty('--ce-fx-y', y.toFixed(1) + 'px');
      if (el.dataset.ceOpacity) el.style.setProperty('--ce-fx-a', clamp(parseFloat(el.dataset.ceOpacity) || 1, 0, 1));
      this.geo = 1;
      if (Math.abs(R - (this.want || 0)) > 48) { this.want = R; el.style.setProperty('--ce-fx-r', R.toFixed(0) + 'px'); this._fit(); }
    }
    _fit() {                                        // within a screen of the viewport: drawn · away from it for 20 seconds: the memory is given back
      clearTimeout(this.bye);
      if (!this.near) {
        if (this.R) this.bye = setTimeout(() => { this.R = 0; this.cv.forEach((c) => { c.width = c.height = 0; }); this.el.classList.remove('is-live'); this._sync(); }, 2e4);
      } else if (this.want && this.R !== this.want && !this.due) {
        this.due = 1;
        idle(() => this._draw(0, this.seed, this.want));
      }
    }
    _draw(i, seed, R) {                             // one ring per idle moment, so the page is never held up for long
      if (!this.near || !all.has(this)) { this.due = 0; return; }
      const t0 = performance.now(), d = this.el.dataset, ink = INKS[d.ceTone] || INKS['blue-on-light'];
      // sharp on sharp screens, within a memory budget: the ring around the eye at the screen's own
      // resolution, the two big rings sharing what is left (never under one canvas pixel per CSS pixel)
      const side = RINGS.map((g) => Math.ceil(R * g[1] * 2) + 24), top = clamp(W.devicePixelRatio || 1, 1, 2);
      const budget = phone.matches ? 4e6 : navigator.deviceMemory <= 4 ? 8e6 : 14e6;       // canvas pixels (4 bytes each)
      const wide = clamp(Math.sqrt(Math.max(0, budget - side[0] * side[0] * top * top) / (side[1] * side[1] + side[2] * side[2])), 1, top);
      const rnd = () => (seed = seed * 48271 % 2147483647) / 2147483647;
      const [r0, r1, n] = RINGS[i], size = side[i], dpr = i ? wide : top, cv = this.cv[i], ctx = cv.getContext('2d'), c = size / 2, arm0 = rnd() * TAU;
      cv.width = cv.height = Math.round(size * dpr);
      cv.style.width = cv.style.height = size + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      for (let k = Math.round(n * clamp(parseFloat(d.ceDensity) || 1, .25, 2.5)); k--;) {
        const at = r0 + (r1 - r0) * Math.pow(rnd(), .85), kind = rnd(), fine = kind < .74, ribbon = kind > .92;
        const pitch = PITCH + rnd() * PITCH_VAR;
        // most streaks gather on one of the arms (a spiral itself: its angle advances with the log of the radius)
        const onArm = arm0 + Math.floor(rnd() * ARMS) * TAU / ARMS - Math.log(at) / (PITCH + PITCH_VAR / 2) + (rnd() + rnd() + rnd() - 1.5) * TAU / ARMS * .42;
        const a = rnd() < .62 ? onArm : rnd() * TAU;
        const w = (fine ? .8 + rnd() * 1.5 : ribbon ? 9 + rnd() * 16 : 2.5 + rnd() * rnd() * 9) * (.5 + at);
        const fade = Math.min(1, (at - .06) / .1) * (1 - Math.pow(clamp((at - .45) / .55, 0, 1), 1.6));   // nothing at the eye, nothing at the far edge
        ctx.globalAlpha = (fine ? .3 + rnd() * .4 : ribbon ? .07 + rnd() * .1 : .13 + rnd() * .24) * fade * ink[0];
        ctx.fillStyle = ink[1 + Math.floor(rnd() * (ink.length - 1))];
        streak(ctx, c, R * at, a, fine ? .7 + rnd() * 1.9 : ribbon ? .9 + rnd() * 1.3 : .5 + rnd() * 1.3, pitch, w, fine ? .6 : .85);
      }
      stat.lines = Math.max(stat.lines, performance.now() - t0);
      if (i < 2) return idle(() => this._draw(i + 1, seed, R));
      this.due = 0; this.R = R;
      this.el.classList.add('is-live');
      this._sync();
      if (this.want !== R) this._fit();             // the box changed while it was being drawn
    }
    _sync() {                                       // the rings turn only while they can be seen
      const run = this.R && this.seen && !this.paused && !D.hidden && !reduce.matches;
      if (run && !this.an) {
        this.an = this.cv.map((c, i) => {
          const ms = RINGS[i][3] * 1000, a = c.animate([{ transform: 'translate(-50%,-50%) rotate(0deg)' }, { transform: 'translate(-50%,-50%) rotate(360deg)' }], { duration: ms, iterations: Infinity });
          a.currentTime = (this.seed * (i + 3) % 997) / 997 * ms;                  // each ring starts somewhere else every visit
          return a;
        });
      }
      if (!this.an) return;
      if (reduce.matches) { this.an.forEach((a) => a.cancel()); this.an = 0; rest(this); return; }
      this.an.forEach((a) => (run ? a.play() : a.pause()));
      if (run && (this.held || this.rate !== 1 || performance.now() < this.until)) wake(this); else rest(this);
    }
    tick(t, dt) {                                   // only while spinning up or easing back
      const to = t < this.until ? this.burst : this.held ? 9 : 1;
      this.rate = ease(this.rate, to, dt, 3.2);
      if (t >= this.until && Math.abs(this.rate - to) < .004) { this.rate = to; rest(this); }
      if (this.an) this.an.forEach((a) => (a.updatePlaybackRate ? a.updatePlaybackRate(this.rate) : (a.playbackRate = this.rate)));
    }
    destroy() {
      if (this.an) this.an.forEach((a) => a.cancel());
      this.cv.forEach((c) => c.remove());
      this.el.classList.remove('is-live');
      untrack(this);
    }
  }

  /* ------------------------------------------------------------------
     The water. One full-screen triangle; everything happens in the fragment shader.
     The picture is built in spiral coordinates: `A` is constant along a streamline, `LR` runs
     along it. Three trains of waves follow the spiral (a slow three-armed swell, ripples riding
     it, fine silk); the shader keeps their SLOPES, lights the surface from the upper left and adds
     small glints: that is what makes it read as liquid rather than smoke. A few foam threads are
     streamlines themselves, so they can never close into a loop. Everything dies away with
     distance from the eye: a few dial-widths out the water lies still and dark, which is where
     the words go. The canvas covers only that living pool; the still water around it is the
     element's own background colour.
     ------------------------------------------------------------------ */
  const VERT = 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}';
  const FRAG = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform vec2 uC;       // the eye, canvas px
uniform float uU;      // canvas px in one dial radius
uniform vec4 uR, uF;   // per layer: rotation (cells) · flow along the spiral (cells)
uniform vec4 uS;       // x speed 0..1.2 · y burst 0..1 · z reach (dial radii until the water lies still) · w foam
uniform vec4 uB;       // from the eye to the left, bottom, right and top edge (dial radii); 1000 = open water
uniform vec3 uK[5];    // abyss, still water, body, lit, foam
const float TAU = 6.2831853;
const vec2 SUN = vec2(-.5, .866);                       // the light comes from the upper left
const vec3 HALF = vec3(-.189, .328, .925);              // halfway between that light (45 degrees up) and the eye

float hash(vec2 p) { p = fract(p * vec2(.3291, .2713) + .137); p += dot(p, p.yx + 17.31); return fract(p.x * p.y * 43.17 + p.x + p.y); }

// value noise and its gradient (quintic, so the slopes are smooth too);
// wraps every "per" cells around the circle and every 64 along the flow
vec3 nd(vec2 x, float per) {
  vec2 i = floor(x), f = fract(x), u = f * f * f * (f * (f * 6. - 15.) + 10.), du = 30. * f * f * (f * (f - 2.) + 1.);
  vec2 cx = mod(vec2(i.x, i.x + 1.), per), cy = mod(vec2(i.y, i.y + 1.), 64.);
  float a = hash(vec2(cx.x, cy.x)), b = hash(vec2(cx.y, cy.x)), c = hash(vec2(cx.x, cy.y)), d = hash(vec2(cx.y, cy.y));
  float k = a - b - c + d;
  return vec3(a + (b - a) * u.x + (c - a) * u.y + k * u.x * u.y, du * (vec2(b - a, c - a) + k * u.yx));
}

void main() {
  vec2 p = (gl_FragCoord.xy - uC) / uU;
  float sp = min(uS.x, 1.), px = 1. / uU;
  float R = length(p) + 1e-4;
  vec2 ER = p / R, ET = vec2(-ER.y, ER.x);
  float LR = log(R + .02);
  float K = mix(.7, 1.9, sp);                           // faster = a tighter spiral
  float A = (atan(p.y, p.x) - K * LR) / TAU;            // one unit per turn; constant along a streamline
  vec2 DA = (ET - K * ER) / TAU;                        // R * (screen gradient of A); that of LR is ER

  float far = smoothstep(.75, uS.z, R);                 // 0 at the dial · 1 where the water lies still
  // live water is still by the time it reaches the band above or below it (no seam against a neighbour);
  // an edge a thousand radii away (every edge of a still picture, the sides of a footer) is open water
  vec4 shore = smoothstep(vec4(0.), max(clamp(uB - 1.2, 0., .9), 1e-3), vec4(p.x + uB.x, p.y + uB.y, uB.z - p.x, uB.w - p.y));
  float inland = shore.x * shore.y * shore.z * shore.w;
  float live = (1. - far) * (1. - far) * inland;

  // the funnel
  float we = mix(.27, .43, sp);                         // it widens with speed
  float q = clamp(R / (we * 1.3), 0., 1.);              // 0 at the bottom · 1 at the lip
  float open = smoothstep(we * .3, we * 1.8, R);        // 0 deep in the funnel · 1 on open water
  float side = -dot(ER, SUN);                           // +1 on the wall that faces the light

  // the swell: three broad arms
  vec3 w = nd(vec2(A * 3. + uR.x, LR * .5 + uF.x), 3.);
  float bend = w.x - .5;
  vec2 gw = w.y * 3. * DA + w.z * .5 * ER;
  float lit = dot(gw, SUN);
  // the folds riding it: seven around the circle, bent by the swell. Each is a crease of the surface,
  // lit on the side turned to the light and in shadow on the other, with a crisp edge between the two
  vec3 c = nd(vec2(A * 7. + uR.y + bend * 1.4, LR * .3 + uF.y), 7.);
  vec2 gc = c.y * (7. * DA + 1.4 * gw) + c.z * .3 * ER;
  float crest = abs(c.x - .5) * R / (length(gc) + .03); // distance to the nearest crease, dial radii
  float fold = exp(-crest / (.11 * R + .02)) * clamp(sign(c.x - .5) * dot(gc, SUN) * .5, -1., 1.);
  // silk: fine ripples between the folds, and the glints they throw
  vec3 f = nd(vec2(A * 24. + uR.z + bend * 2.4, LR * 1.6 + uF.z), 24.);
  vec2 gf = f.y * (24. * DA + 2.4 * gw) + f.z * 1.6 * ER;
  float glint = pow(max(dot(normalize(vec3((gw * .5 + gf * .05) * 1.25, 1.)), HALF), 0.), 40.);

  // tone: 0 abyss · 1 still water · 2 body · 3 lit
  float arms = live * open;
  float lip = smoothstep(we * .45, we * 1.15, R) * (1. - smoothstep(we * 1.1, we * 3.4, R)) * inland;
  float T = 1. + live * .62
    + arms * (bend * 1.15 + lit * .3 + smoothstep(.25, 1.1, lit) * .16 + fold * .42 + (f.x - .5) * .14 + glint * .55)
    + lip * (.5 + .42 * side + .3 * sp + .45 * uS.y);
  T = mix(.1, T, q * q * (3. - 2. * q)) + side * q * (1. - q);   // it darkens all the way down; the far wall catches the light
  T = clamp(T, 0., 3.35);
  vec3 col = mix(uK[0], uK[1], clamp(T, 0., 1.));
  col = mix(col, uK[2], clamp(T - 1., 0., 1.));
  col = mix(col, uK[3], clamp(T - 2., 0., 1.));
  col = mix(col, uK[4], clamp(T - 3., 0., 1.) * .5);

  // foam threads: streamlines (so they never close into a loop), wobbling a little, showing in stretches
  // that travel with the flow, never inside the eye
  vec3 v = nd(vec2(A * 5. + uR.w, LR * .7 + uF.w), 5.);
  vec2 g = (11. + v.y * 4.) * DA + v.z * .56 * ER;
  float d = abs(fract(A * 11. + uR.w * 2.2 + v.x * .8) - .5) * R / length(g);        // distance to the nearest thread, dial radii
  float thread = (1. - smoothstep(.001, .001 + 1.5 * px, d)) * smoothstep(.4, .78, w.x) * smoothstep(.3, .66, c.x) * (.5 + .5 * sp + .4 * uS.y);
  float rim = (1. - smoothstep(0., .012 + 2. * px, abs(R - we * 1.3))) * pow(max(side, 0.), 2.) * .4;   // the lip catches the light
  col = mix(col, uK[4], clamp(max(thread * arms, rim * inland), 0., 1.) * uS.w);

  col += (hash(gl_FragCoord.xy * .37) - .5) / 128. * (1. - smoothstep(.9, 1., far));   // dither: no banding in the long dark gradients
  gl_FragColor = vec4(col, 1.);
}`;

  // per tone: abyss, still water, body, lit, foam · foam strength
  const TONES = {
    navy: ['0611200b1f330a4a850a74c6bfe3fb', .5],
    blue: ['06386108599c0a74c63fa4ecffffff', .55]
  };
  const toneOf = (name) => {
    const t = TONES[name] || TONES.navy;
    if (!t.k) t.k = new Float32Array(t[0].match(/../g).map((h) => parseInt(h, 16) / 255));
    return t;
  };

  /* ---------- Flow: the speed state a dial and the water share. Liquid has inertia. ---------- */
  class Flow {
    constructor(el, speed) {
      this.el = el;
      this.speed = this.flow = clamp(+speed || 3, 1, 10);
      this.burst = 0; this.pulsing = false; this.held = false; this.until = 0;
    }
    setSpeed(n) {
      n = clamp(+n || 1, 1, 10);
      if (n !== this.speed) { this.speed = n; this._say(); }
    }
    pulse(ms) { this.until = performance.now() + (ms > 0 ? ms : 700); this._pulse(); }
    hold(on) { this.held = !!on; if (on) this.until = performance.now() + 520; this._pulse(); }   // the PULSE button
    _pulse() {
      clearTimeout(this.timer);
      const left = this.until - performance.now(), on = this.held || left > 0;
      if (!this.held && left > 0) this.timer = setTimeout(() => this._pulse(), left + 8);
      if (on !== this.pulsing) { this.pulsing = on; this._say(); }
    }
    _say() {
      this.el.classList.toggle('is-pulsing', this.pulsing);
      fire(this.el, 'ce:fx:speed', { speed: this.speed, value: Math.round(this.speed), pulsing: this.pulsing });
      this._sync();
    }
    _sync() {}
    step(t, dt) {                                   // spins up fast, coasts down slowly
      if (t === this.t) return;
      this.t = t;
      const to = this.pulsing ? 11 : this.speed;
      if (reduce.matches) { this.flow = to; this.burst = +this.pulsing; return; }
      this.flow = ease(this.flow, to, dt, to > this.flow ? 5 : 1.2);
      this.burst = ease(this.burst, +this.pulsing, dt, 5);
    }
  }

  /* ---------- Water ---------- */
  let running = 0;                                  // one water per page runs; any other is a still frame
  // Adaptive quality: now and then one frame is timed from the draw call to its last pixel. Slower than
  // 12 ms lowers the resolution (twice at most); still slower than 22 ms after that freezes the water
  // to one sharp still frame. ?bofxdebug=slow|slower multiply the measured time, to watch it happen.
  const lag = { slow: 4, slower: 40 }[debug] || 1;
  let adapt = debug !== 'noadapt', settled = D.readyState === 'complete';
  if (!settled) W.addEventListener('load', () => setTimeout(() => { settled = true; }, 1500));

  class Water extends Flow {
    constructor(el) {
      super(el, el.dataset.ceSpeed);
      const c = this.canvas = D.createElement('canvas');
      this.tone = toneOf(el.dataset.ceTone);
      this.fixed = 'ceStill' in el.dataset || running > 0;
      if (!this.fixed) running++;
      this.turn = +el.dataset.ceSeed || (dice() % 1000) / 100;
      this.ph = 11 + this.turn; this.shown = 0; this.n = 0; this.best = 1e9;
      c.className = 'ce-fx__canvas';
      el.appendChild(c);
      track(this);
    }
    pause() { this.paused = true; this._sync(); }
    resume() { this.paused = false; this._sync(); }

    _fit() {                                        // once, when within a viewport of the screen, after a painted frame, in idle time
      if (this.booted || !this.near) return;
      this.booted = 1;
      requestAnimationFrame(() => idle(() => {
        const c = this.canvas;
        let gl;
        this.t0 = performance.now();
        try { gl = c.getContext('webgl', { alpha: false, antialias: false, depth: false, stencil: false, powerPreference: 'low-power' }); } catch (e) {}
        if (!gl || !this.el.isConnected) return;     // no WebGL: the CSS look stays
        this.gl = gl;
        c.addEventListener('webglcontextlost', (e) => { e.preventDefault(); this.ok = 0; rest(this); this.el.classList.remove('is-live'); });
        c.addEventListener('webglcontextrestored', () => { this.t0 = performance.now(); this._program(); });
        idle(() => this._program());                 // (its own idle moment: two short jobs, not one long one)
      }));
    }
    _program() {
      const gl = this.gl;
      if (!all.has(this) || gl.isContextLost()) return;
      const p = gl.createProgram(), par = gl.getExtension('KHR_parallel_shader_compile');
      [[gl.VERTEX_SHADER, VERT], [gl.FRAGMENT_SHADER, FRAG]].forEach(([type, src]) => {
        const s = gl.createShader(type);
        gl.shaderSource(s, src); gl.compileShader(s); gl.attachShader(p, s);
      });
      gl.bindAttribLocation(p, 0, 'p');
      gl.linkProgram(p);
      const done = () => {
        if (!all.has(this) || gl.isContextLost()) return;
        if (par && !gl.getProgramParameter(p, par.COMPLETION_STATUS_KHR)) return setTimeout(done, 16);   // the driver is still compiling, in the background
        if (!gl.getProgramParameter(p, gl.LINK_STATUS)) { if (debug) console.warn('ce-fx', gl.getProgramInfoLog(p), gl.getShaderInfoLog(gl.getAttachedShaders(p)[1])); return; }
        gl.useProgram(p);
        gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
        gl.enableVertexAttribArray(0);
        gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
        const u = this.u = {};
        'uC uU uR uF uS uB uK'.split(' ').forEach((n) => { u[n] = gl.getUniformLocation(p, n); });
        gl.uniform3fv(u.uK, this.tone.k);
        stat.compile = Math.max(stat.compile, performance.now() - this.t0);   // from asking for WebGL to a ready shader
        this.ok = 1;
        this._size();
        this._sync();
        this.el.classList.add('is-live');
      };
      done();
    }

    _measure() {                                    // reads only (see sized): the element, and the dial its eye follows
      const a = this.anchor;
      this.m = { b: this.el.getBoundingClientRect(), r: a && a.isConnected ? a.getBoundingClientRect() : null };
    }
    _size(measured) {                               // place the eye; lay the canvas over the living pool only
      if (!measured || !this.m) this._measure();
      const el = this.el, d = el.dataset, { b, r } = this.m, w = b.width, h = b.height, small = phone.matches;
      if (w < 2 || h < 2) return;
      let x, y, unit;
      if (r) { x = r.left + r.width / 2 - b.left; y = r.top + r.height / 2 - b.top; unit = r.width * .35; }   // the dial's track
      else { const c = centre(d, .72, .5); x = w * c[0]; y = h * c[1]; unit = Math.min(w, h) * .3 * (parseFloat(d.ceSize) || 1); }
      this.geo = 1;
      el.style.setProperty('--ce-fx-x', x.toFixed(1) + 'px');     // the CSS fallback keeps the same eye
      el.style.setProperty('--ce-fx-y', y.toFixed(1) + 'px');
      el.style.setProperty('--ce-fx-u', unit.toFixed(1) + 'px');
      if (!this.ok) return;
      const gl = this.gl, c = this.canvas, reach = this.reach = clamp(parseFloat(d.ceReach) || 4.2, 1.5, 12), far = reach * unit;
      const x0 = Math.max(0, Math.floor(x - far)), y0 = Math.max(0, Math.floor(y - far));
      const bw = Math.min(Math.ceil(w), Math.ceil(x + far)) - x0, bh = Math.min(Math.ceil(h), Math.ceil(y + far)) - y0;
      c.style.cssText = `left:${x0}px;top:${y0}px;width:${Math.max(bw, 0)}px;height:${Math.max(bh, 0)}px`;
      if (bw < 2 || bh < 2) return;                 // the pool lies outside the element
      let q = parseFloat(d.ceQuality);              // tools only: exact canvas px per CSS px
      if (!q) q = Math.min(W.devicePixelRatio || 1, small ? 1.25 : 1.5, Math.sqrt((small ? 8e5 : 2.2e6) / (bw * bh))) * stat.scale;
      const cw = Math.max(2, Math.round(bw * q)), ch = Math.max(2, Math.round(bh * q));
      if (c.width !== cw || c.height !== ch) { c.width = cw; c.height = ch; gl.viewport(0, 0, cw, ch); }
      q = cw / bw;
      gl.uniform2f(this.u.uC, (x - x0) * q, ch - (y - y0) * q);
      gl.uniform1f(this.u.uU, unit * q);
      // live water is still by the time it reaches the band above or below; a still is a picture and may run off every edge
      gl.uniform4f(this.u.uB, 1e3, this.fixed ? 1e3 : (h - y) / unit, 1e3, this.fixed ? 1e3 : y / unit);
      this._draw(busy.has(this));
    }

    _sync() {                                       // run, or hold one still frame
      if (!this.ok) return;
      const run = this.seen && !this.paused && !this.fixed && !D.hidden && !reduce.matches && stat.state !== 'frozen';
      if (run) wake(this);
      else { rest(this); if (!this.paused) this._draw(); }
    }
    tick(t, dt) {
      if (!this.el.isConnected) return this.destroy();
      this.step(t, dt);
      const s = clamp((this.flow - 1) / 9, 0, 1.2);
      this.turn += dt * (.006 + .16 * Math.pow(s, 1.5));         // turns of the swell per second
      this.ph += dt * (.02 + .5 * Math.pow(s, 1.3));
      if (t - this.shown < (s < .5 && !this.pulsing ? 30 : 12)) return;    // calm water is drawn 30 times a second, busy water 60
      this.shown = t;
      this._draw(true);
    }
    _draw(moving) {                                 // a still (not moving) shows where the dial points, not where the water was
      if (!this.ok) return;
      const gl = this.gl, u = this.u, t = this.turn, f = this.ph;
      const s = moving ? clamp((this.flow - 1) / 9, 0, 1.2) : ((this.pulsing ? 10 : this.speed) - 1) / 9;
      gl.uniform4f(u.uR, (t * 3) % 3, (t * 10.15) % 7, (t * 48) % 24, (t * 8.5) % 5);       // each layer turns at its own rate
      gl.uniform4f(u.uF, (f * .25) % 64, (f * .2) % 64, (f * .7) % 64, (f * .5) % 64);
      gl.uniform4f(u.uS, s, moving ? this.burst : +this.pulsing, this.reach, this.tone[1]);
      // three timings at the start (once the page has loaded) and again every 1800 draws: frames 12, 24 and 36
      const n = moving && settled ? ++this.n % 1800 || 1 : 1;
      if (n % 12 || !(debug || (adapt && n <= 36))) return gl.drawArrays(gl.TRIANGLES, 0, 3);
      const t0 = performance.now();
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(4));   // waits for the last pixel
      this.best = Math.min(this.best, stat.draw = (performance.now() - t0) * lag);
      if (!adapt || n !== 36) return;               // judge the quickest of the three: a hitch is not a slow chip
      const best = this.best;
      this.best = 1e9;
      if (best < 12) return;
      if (stat.drops < 2) { stat.drops++; stat.scale *= clamp(Math.sqrt(9 / best), .6, .85); this.n = 0; }
      else if (best > 22) { stat.scale = 1; stat.state = 'frozen'; adapt = false; }
      else return;
      this._size();
      this._sync();
    }
    destroy() {
      const x = this.gl && this.gl.getExtension('WEBGL_lose_context');
      if (!this.fixed) running--;
      this.ok = 0;
      untrack(this);
      if (x) x.loseContext();
      this.canvas.remove();
      this.el.classList.remove('is-live');
    }
  }

  /* ---------- <ce-dial>: the 1-10 instrument around its content ---------- */
  const ANG = (v) => -135 + (v - 1) * 30;                                    // degrees, clockwise from 12 o'clock
  const PT = (deg, r) => { const a = deg * Math.PI / 180; return (r * Math.sin(a)).toFixed(1) + ' ' + (-r * Math.cos(a)).toFixed(1); };
  const ARC = (r, a, b) => `M${PT(a, r)}A${r} ${r} 0 ${b - a > 180 ? 1 : 0} 1 ${PT(b, r)}`;
  const INOUT = (k) => (k < .5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2);
  const OUT = (k) => 1 - Math.pow(1 - k, 3);

  class Dial extends HTMLElement {
    connectedCallback() {
      if (this.svg) return;
      const to = D.getElementById(this.getAttribute('for'));
      const want = clamp(Math.round(+this.getAttribute('value') || 3), 1, 10);
      const fx = this.fx = (to && api.get(to)) || new Flow(this, want);
      let minor = '', majors = '', nums = '';
      for (let i = 0; i <= 36; i++) {
        const d = -135 + i * 7.5;
        if (i % 4) minor += `M${PT(d, 162)}L${PT(d, 167)}`;
        else majors += `<path class="ce-dial__tick" d="M${PT(d, 159)}L${PT(d, 171)}"/>`;
      }
      for (let v = 1; v <= 10; v++) nums += `<g class="ce-dial__num" data-v="${v}" transform="translate(${PT(ANG(v), 190)})"><circle r="22"/><text dy=".36em">${v}</text></g>`;
      this.insertAdjacentHTML('beforeend',
        `<svg class="ce-dial__svg" viewBox="-212 -212 424 424" aria-hidden="true" focusable="false">` +
        `<path class="ce-dial__track" d="${ARC(148, -135, 135)}"/>` +
        `<path class="ce-dial__minor" d="${minor}"/>${majors}` +
        `<path class="ce-dial__flow" pathLength="9" d="${ARC(148, -135, 135)}" stroke-dasharray="0 10"/>` +
        `<circle class="ce-dial__hit" r="166"/>${nums}` +
        `<g class="ce-dial__grip"><circle class="ce-dial__grab" r="30"/><circle class="ce-dial__ring" r="17"/><circle class="ce-dial__knob" r="10"/><circle class="ce-dial__dot" r="3.6"/></g>` +
        `</svg><input class="ce-dial__range" type="range" min="1" max="10" step="1" value="${want}">`);
      const svg = this.svg = this.lastChild.previousSibling, range = this.range = this.lastChild, q = (s) => svg.querySelector(s);
      const hit = q('.ce-dial__hit'), grip = this.grip = q('.ce-dial__grip');
      this.arc = q('.ce-dial__flow');
      this.nums = [...svg.querySelectorAll('.ce-dial__num')];
      this.majors = [...svg.querySelectorAll('.ce-dial__tick')];
      range.setAttribute('aria-label', this.getAttribute('label') || 'Water speed, 1 to 10');
      this.pos = this.goal = want;
      if (this.hasAttribute('autosweep') && !reduce.matches) { this.sweep = want; this.pos = this.goal = fx.speed = fx.flow = 1; }   // starts at rest
      else fx.setSpeed(want);
      if (fx.canvas) fx.anchor = this;                       // the water's eye follows the dial

      const at = (e) => { const b = svg.getBoundingClientRect(); return 1 + (clamp(Math.atan2(e.clientX - b.left - b.width / 2, b.top + b.height / 2 - e.clientY) * 57.2958, -135, 135) + 135) / 30; };
      const aim = (e) => { const v = at(e); if (Math.abs(v - this.goal) < 5) this.goal = v; this._stir(); };   // never jump across the gap at the bottom
      const down = (e) => {
        if (e.button) return;
        this.tw = 0; this.drag = 1; this.goal = this.pos;
        this.classList.add('is-dragging');
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
        this.goal = at(e);
        this._stir();
        e.preventDefault();
      };
      const up = () => { if (this.drag) { this.drag = 0; this.classList.remove('is-dragging'); this.goal = Math.round(this.goal); this._stir(); } };
      hit.addEventListener('pointerdown', (e) => { if (e.pointerType === 'mouse') down(e); });    // touch drags the grip only, so the page still scrolls
      grip.addEventListener('pointerdown', down);
      [hit, grip].forEach((n) => { n.addEventListener('pointermove', (e) => { if (this.drag) aim(e); }); n.addEventListener('pointerup', up); n.addEventListener('pointercancel', up); });
      svg.addEventListener('click', (e) => { const g = e.target.closest('.ce-dial__num'); if (g) this._to(+g.dataset.v, 480, OUT); });
      range.addEventListener('input', () => this._to(+range.value, 260, OUT));
      ['focus', 'blur'].forEach((n) => range.addEventListener(n, () => this.classList.toggle('is-focus', n === 'focus' && range.matches(':focus-visible'))));
      fx.el.addEventListener('ce:fx:speed', this.heard = (e) => {
        if (e.target !== fx.el) return;
        this.classList.toggle('is-pulsing', fx.pulsing);
        if (!this.mine && !this.tw && !this.drag && fx.speed !== this.pos) this.pos = this.goal = fx.speed;   // someone else set the speed
        if (this.live) wake(this);
      });

      (this.ro = new ResizeObserver(() => {
        if (fx._measure) fx._measure();                        // the water's reads before this dial's writes (fix-speed 02-10)
        const px = this.clientWidth / 424 || .5;               // screen px per dial unit
        svg.style.setProperty('--ce-dial-num', (clamp(this.clientWidth * .052, 13, 20) / px).toFixed(1) + 'px');
        svg.style.setProperty('--ce-dial-grip', clamp(.7 / px, .8, 1.5).toFixed(2));
        q('.ce-dial__grab').setAttribute('r', (24 / px).toFixed(0));
        this.nums.forEach((g) => g.firstChild.setAttribute('r', Math.min(46, 22 / px).toFixed(0)));
        if (fx._size) fx._size(true);
      })).observe(this);
      (this.io = new IntersectionObserver(([en]) => {
        this.live = en.isIntersecting;
        if (!this.live) {
          if (this.auto) {                                     // the sweep went unseen: it starts again next time
            this.auto = this.tw = 0; this.pos = this.goal = 1; this.mine = 1; fx.setSpeed(1); this.mine = 0;
            this.classList.remove('is-awake');
          }
          return rest(this);
        }
        wake(this);
        if (this.sweep && !this.auto) {                        // first sight: a gentle 1 to 6, a breath, then settle
          const v = this.sweep, run = this.auto = performance.now(), on = () => this.auto === run;
          this.classList.add('is-awake');
          setTimeout(() => on() && this._to(6, 1700, INOUT, () => setTimeout(() => on() && this._to(v, 1500, INOUT, () => { this.sweep = 0; this._stir(1); }, 1), 380), 1), 350);
        }
      }, { threshold: .35 })).observe(this);
      this._paint(0);
      fx._say();                                             // readouts start on the right number
    }
    disconnectedCallback() { rest(this); }
    get value() { return this.v; }
    set value(n) { this._to(clamp(Math.round(n), 1, 10), 480, OUT); }

    _stir(done) {                                   // the visitor (or the sweep) has the dial: wake it, then let it go quiet again
      if (!done) this.auto = this.sweep = 0;
      this.classList.add('is-awake');
      clearTimeout(this.nap);
      this.nap = setTimeout(() => this.classList.remove('is-awake'), done ? 900 : 2600);
      wake(this);
    }
    _to(v, ms, curve, then, auto) {                 // travel to a value; auto = the first-sight sweep (a touch cancels it)
      if (!auto) this._stir();
      if (reduce.matches) { this.tw = 0; this.pos = this.goal = v; this.fx.setSpeed(v); wake(this); if (then) then(); return; }
      this.tw = { a: this.pos, b: v, t: performance.now(), ms, curve, then };
      wake(this);
    }
    tick(t, dt) {
      const fx = this.fx, tw = this.tw;
      if (!this.isConnected) return rest(this);
      if (tw) {
        const k = clamp((t - tw.t) / tw.ms, 0, 1);
        this.pos = this.goal = tw.a + (tw.b - tw.a) * tw.curve(k);
        if (k === 1) { this.tw = 0; if (tw.then) tw.then(); }
      } else if (this.pos !== this.goal) {                     // a weighted grip: it follows the hand, then settles on the numeral
        this.pos = ease(this.pos, this.goal, dt, this.drag ? 20 : 11);
        if (Math.abs(this.pos - this.goal) < .004) this.pos = this.goal;
      }
      this.mine = 1;
      fx.setSpeed(this.pos);
      this.mine = 0;
      fx.step(t, dt);
      this._paint();
      if (!this.tw && !this.drag && this.pos === this.goal && Math.abs(fx.flow - fx.speed) < .01) rest(this);
    }
    _paint() {
      const fx = this.fx, pos = this.pos, n = clamp(fx.flow, 1, 10), v = Math.round(pos), was = this.n0;
      if (pos !== this.p0) { this.p0 = pos; this.grip.setAttribute('transform', `translate(${PT(ANG(pos), 148)})`); }
      if (was === undefined || Math.abs(n - was) > .002) {
        this.n0 = n;
        this.arc.setAttribute('stroke-dasharray', (n - 1).toFixed(3) + ' 10');
        if (was && !reduce.matches && this.animate) {        // a detent: each numeral the water's speed passes gives a small tick
          for (let i = Math.floor(Math.min(n, was)) + 1; i <= Math.min(10, Math.max(n, was)); i++) {
            this.nums[i - 1].lastChild.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.32)' }, { transform: 'scale(1)' }], { duration: 320, easing: 'ease-out' });
            this.majors[i - 1].animate([{ strokeWidth: '2px', opacity: .7 }, { strokeWidth: '5px', opacity: 1 }, { strokeWidth: '2px', opacity: .7 }], { duration: 320, easing: 'ease-out' });
          }
        }
      }
      if (v !== this.v) {
        this.v = v;
        this.range.value = v;
        this.range.setAttribute('aria-valuetext', `Speed ${v} of 10`);
        this.nums.forEach((g, i) => g.classList.toggle('is-on', i + 1 === v));
        this.setAttribute('value', v);
        fire(this, 'ce:dial:change', { value: v });
      }
    }
  }

  /* ---------- PULSE buttons and speed readouts (delegated: nothing to re-bind) ---------- */
  const find = (el) => (el && (el.fx || byEl.get(el))) || null;                   // an instance that already exists
  const flowFor = (n, make) => {
    const id = n.getAttribute('for') || n.dataset.ceSpeedReadout;
    const t = id ? D.getElementById(id) : (n.closest('.shopify-section') || D).querySelector('ce-dial,[data-ce-fx="water"]');
    return make ? api.get(t) : find(t);
  };
  let held = null, pressed = 0;
  const press = (e) => {
    const b = e.target.closest && e.target.closest('[data-ce-pulse]'), fx = b && flowFor(b, 1);
    if (!fx || e.button || e.repeat || (e.key && e.key !== ' ' && e.key !== 'Enter')) return;
    pressed = performance.now();
    if (e.type === 'click') return fx.pulse(800);
    held = fx;
    fx.hold(true);
  };
  const release = (e) => { if (held && (e.type !== 'blur' || e.target === W)) { held.hold(false); held = null; pressed = performance.now(); } };   // (blur: only the window's own)
  D.addEventListener('pointerdown', press);
  D.addEventListener('keydown', press);
  D.addEventListener('click', (e) => { if (performance.now() - pressed > 1200) press(e); });    // assistive tech sends a bare click
  ['pointerup', 'pointercancel', 'keyup', 'blur'].forEach((n) => W.addEventListener(n, release, true));
  D.addEventListener('ce:fx:speed', (e) => {
    const fx = find(e.target);
    if (fx) D.querySelectorAll('[data-ce-speed-readout],[data-ce-pulse]').forEach((n) => {
      if (flowFor(n) !== fx) return;
      (n.closest('.ce-fx-readout') || n).classList.toggle('is-pulsing', fx.pulsing);
      if ('ceSpeedReadout' in n.dataset && n.textContent !== String(e.detail.value)) n.textContent = e.detail.value;
    });
  });

  /* ---------- the pulse button of the lines: [data-ce-pulse-target="#id"] ---------- */
  const wired = new WeakSet();
  const wire = (b) => {
    if (wired.has(b)) return;
    wired.add(b);
    const fx = () => { try { return api.get(D.querySelector(b.dataset.cePulseTarget)); } catch (e) { return null; } };
    const hold = (on) => () => { const o = fx(); if (o && o.spinUp) o.hold(on); };
    b.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') hold(true)(); });    // a finger has no hover: a tap is the press below
    b.addEventListener('pointerleave', hold(false));
    b.addEventListener('focus', () => { if (b.matches(':focus-visible')) hold(true)(); });
    b.addEventListener('blur', hold(false));
    b.addEventListener('pointerdown', () => { const o = fx(); if (o) (o.spinUp ? o.spinUp(14, 900) : o.pulse(900)); });
  };

  /* ---------- wave (pause off-screen), dots (drift with the scroll), rise (one shot) ---------- */
  const calm = new IntersectionObserver((ens) => ens.forEach((en) => en.target.classList.toggle('is-still', !en.isIntersecting)), { rootMargin: '80px 0px' });
  const adrift = new Set();
  let drifting = 0;
  const drift = () => {                             // every read, then every write (fix-speed 02-10)
    drifting = 0;
    const vh = W.innerHeight, list = [...adrift], box = list.map((n) => n.getBoundingClientRect());
    list.forEach((n, k) => n.style.setProperty('--ce-fx-sp', clamp((vh - box[k].top) / (vh + box[k].height), 0, 1).toFixed(4)));
  };
  // in the next frame, not here: this callback runs between layouts, so a read in it laid the page out again
  const afloat = new IntersectionObserver((ens) => { ens.forEach((en) => adrift[en.isIntersecting ? 'add' : 'delete'](en.target)); if (!reduce.matches && !drifting) drifting = requestAnimationFrame(drift); });
  const waiting = new Set();
  let sweeping = 0;
  const rise = (n, passed) => { waiting.delete(n); show.unobserve(n); n.classList.add('is-in'); if (passed) n.classList.add('is-set'); };
  const show = new IntersectionObserver((ens) => ens.forEach((en) => { if (en.isIntersecting) rise(en.target); }), { rootMargin: '0px 0px -7% 0px', threshold: .01 });
  // A fast scroll can carry an element from below the screen to above it between two frames, and an
  // observer never reports that. So while anything is still waiting, a scroll also looks for what has gone by.
  const sweep = () => {                             // every read, then every write (fix-speed 02-10)
    sweeping = 0;
    const line = W.innerHeight * .93, list = [...waiting], box = list.map((n) => n.getBoundingClientRect());
    list.forEach((n, k) => { const r = box[k]; if ((r.width || r.height) && r.top < line) rise(n, r.bottom < 0); });
  };
  W.addEventListener('scroll', () => {
    if (adrift.size && !drifting && !reduce.matches) drifting = requestAnimationFrame(drift);
    if (waiting.size && !sweeping) sweeping = setTimeout(sweep, 90);
  }, { passive: true });
  // Content that is on screen when the script starts is left alone (no blink). A theme that adds the class
  // "ce-fx-js" to <html> in the <head> hides it before the first paint instead, so it rises on load as well.
  const settledRise = () => !H.classList.contains('ce-fx-js') || H.classList.contains('ce-fx-ready');

  /* ---------- public API, boot, theme editor ---------- */
  const KIND = { lines: Lines, water: Water };
  const api = W.CEFx = {
    get: (el) => { const K = el && el.dataset && KIND[el.dataset.ceFx]; return find(el) || (K ? new K(el) : null); },
    init: (root) => {
      root = root || D;
      const late = settledRise();
      // the band that carries a layer is position: relative + isolation: isolate (ce-fx.css .ce-fx-host), and a section
      // with a wave sits above the next one: marked ONCE here. Fix round 10-10: these used to be :has() rules that any
      // element could match, so every small change anywhere (the header's rolling search word, the announcement bar)
      // made the browser restyle the WHOLE page (~5,770 elements, 37-90 ms each, tools/fix_front_invalidation.mjs).
      each(root, '[data-ce-fx="lines"],[data-ce-fx="dots"],[data-ce-fx="water"],[data-ce-fx="wave"],[data-ce-fx="ribbon"],[data-ce-fx="caustics"]', (n) => {
        if (n.parentElement) n.parentElement.classList.add('ce-fx-host');
        if (n.dataset.ceFx !== 'wave') return;
        const sec = n.closest('.shopify-section');
        if (sec) { sec.classList.add('ce-fx-wave-sec'); if (n.dataset.cePos === 'bottom') sec.classList.add('ce-fx-wave-bottom'); }
      });
      each(root, '[data-ce-fx="lines"],[data-ce-fx="water"]', (el) => api.get(el));
      each(root, '[data-ce-fx="wave"]', (n) => { while (n.children.length < 3) n.appendChild(D.createElement('i')); n.setAttribute('aria-hidden', 'true'); calm.observe(n); });
      each(root, '[data-ce-fx="dots"]', (n) => { n.setAttribute('aria-hidden', 'true'); afloat.observe(n); });
      // anything that starts inside the first screen shows now (rises, if the head hid it); only what is below the
      // screen waits (Tommy 01-10: items at the very bottom of the first screen waited for a scroll).
      // fix-speed 02-10: every read, then every write, and no read on a page without one (window.innerHeight alone
      // laid the whole page out at start-up)
      const ups = [];
      each(root, '[data-ce-fx="rise"]:not(.is-in)', (n) => ups.push(n));
      if (ups.length) {
        const vh = W.innerHeight, tops = ups.map((n) => n.getBoundingClientRect().top);
        ups.forEach((n, k) => { if (tops[k] < vh) { if (late) n.classList.add('is-in', 'is-set'); else rise(n); } else { waiting.add(n); show.observe(n); } });
      }
      each(root, '[data-ce-pulse-target]', wire);
    },
    stats: stat
  };
  const again = () => all.forEach((o) => { if (o.canvas) remeasure(o); o._sync(); });   // the water re-measured after the next layout (fix-speed 02-10)
  D.addEventListener('visibilitychange', () => { H.classList.toggle('ce-fx-hidden', D.hidden); again(); });
  W.addEventListener('load', again);
  if (D.fonts && D.fonts.ready) D.fonts.ready.then(again);
  if (reduce.addEventListener) reduce.addEventListener('change', again);
  D.addEventListener('animationend', (e) => all.forEach((o) => { if (o.anchor && e.target.contains && e.target.contains(o.anchor)) remeasure(o); }));   // a dial that rose into place
  D.addEventListener('shopify:section:load', (e) => api.init(e.target));
  D.addEventListener('shopify:section:unload', (e) => {
    all.forEach((o) => e.target.contains(o.el) && o.destroy());
    each(e.target, '[data-ce-fx]', (n) => { [calm, afloat, show].forEach((w) => w.unobserve(n)); adrift.delete(n); waiting.delete(n); });
  });
  const boot = () => { api.init(); H.classList.add('ce-fx-ready'); customElements.define('ce-dial', Dial); };
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', boot); else boot();

  if (debug) {                                         // ?bofxdebug=1 : measured frame time and the resolution scale
    const box = D.createElement('pre');
    box.style.cssText = 'position:fixed;left:8px;bottom:8px;z-index:99999;margin:0;padding:8px 10px;border-radius:6px;background:rgba(0,0,0,.82);color:#9fe3ff;font:12px/1.45 monospace;pointer-events:none;white-space:pre';
    setInterval(() => {
      if (!box.isConnected && D.body) D.body.appendChild(box);
      box.textContent = `ce-fx ${stat.state}${adapt ? '' : ' (fixed)'} | frame ${stat.ms.toFixed(1)} ms = ${(1000 / stat.ms).toFixed(0)} fps | one water draw ${stat.draw.toFixed(1)} ms | scale ${stat.scale.toFixed(2)} | drops ${stat.drops}\n` +
        `shader ready in ${stat.compile.toFixed(0)} ms | slowest ring of lines drawn in ${stat.lines.toFixed(0)} ms\n` +
        [...all].map((o) => (o.canvas ? `water ${o.el.id || ''}: ${o.canvas.width}x${o.canvas.height} ${!o.ok ? 'no webgl yet' : busy.has(o) ? 'running' : 'still'}` : `lines ${o.el.id || ''}: ${o.cv.map((c) => c.width).join('/')} px ${o.an ? 'x' + o.rate.toFixed(2) : 'still'}`)).join('\n');
    }, 400);
  }
})();

/* ==========================================================================
   THE WATER FAMILY (ds, 08-10-2026; docs/DESIGN-SYSTEM.md "Effects"). Ported from the comps (look-c: ribbon +
   caustics). Driven by data attributes; colours and sizes are CSS variables (assets/ce-fx.css section 10).
   (Source of this block: tools/ds_fx_water.js, appended by tools/ds_fx_build.py; edit there and re-run.)

     [data-ce-fx="ribbon"]     layered teal SVG water waves drifting at different speeds along a section edge.
                               class ce-fx-ribbon--bottom (default) or ce-fx-ribbon--top (flipped); CSS variables
                               --ce-ribbon-h (height), --ce-ribbon-back / -main / -crest / -line / -to (colours; -to =
                               the colour of the section the water flows INTO), --ce-ribbon-speed (1 = the comp).
     [data-ce-fx="caustics"]   WebGL water light (~30 fps; paused off screen and in a hidden tab; one still frame
                               under reduced motion), CSS fallback (soft drifting light) when WebGL is missing.
                               data-ce-tone="deep" (default: deep teal, teal light) | "ice" (ice blue, white light)
                               | "cream"; or --ce-caustics-bg / --ce-caustics-light (hex). data-ce-scale="1.15".
     [data-ce-fx="reveal"]     rise + fade in once, as it scrolls into view. Anything already on the first screen
                               shows at once. style="--i: 2" delays it (x 90 ms). data-ce-fx="reveal" with
                               data-ce-stagger: its children rise in turn instead.
   window.CEWater.init(root) binds new markup (done on load and on shopify:section:load).
   Everything is complete and still under prefers-reduced-motion, and without JavaScript.
   ========================================================================== */
(() => {
  'use strict';
  const W = window, D = document, H = D.documentElement;
  if (W.CEWater || !('IntersectionObserver' in W && 'ResizeObserver' in W)) return;
  const reduce = W.matchMedia('(prefers-reduced-motion: reduce)');
  const each = (root, sel, fn) => { if (root.matches && root.matches(sel)) fn(root); root.querySelectorAll(sel).forEach(fn); };
  const NS = 'http://www.w3.org/2000/svg';
  let uid = 0;

  /* ---------- ribbon ---------- */
  const wavePts = (base, amp, phase, period) => {
    const pts = [];
    for (let x = 0; x <= 2880; x += 16) {
      const a = (x / period) * Math.PI * 2 + phase;
      pts.push(x + ',' + (base + amp * Math.sin(a) + amp * 0.28 * Math.sin(a * 2 + phase * 1.7)).toFixed(2));
    }
    return pts;
  };
  const wave = (b, a, ph, per, kind) => {
    let d = 'M' + wavePts(b, a, ph, per).join(' L');
    if (kind === 'fill') d += ' L2880,121 L0,121 Z';
    return d;
  };
  const path = (d, cls) => { const p = D.createElementNS(NS, 'path'); p.setAttribute('d', d); p.setAttribute('class', cls); return p; };
  const group = (cls, kids) => { const g = D.createElementNS(NS, 'g'); g.setAttribute('class', 'ce-rib__flow ' + cls); kids.forEach((k) => g.appendChild(k)); return g; };
  const stop = (off, cls) => { const s = D.createElementNS(NS, 'stop'); s.setAttribute('offset', off); s.setAttribute('class', cls); return s; };
  const ribbons = new Set();
  // the viewBox follows the window (560 to 1440 wide), so a wave keeps its shape on a phone instead of squashing
  const fit = (svg) => { const vb = Math.round(Math.min(1440, Math.max(560, W.innerWidth))); svg.setAttribute('viewBox', '0 0 ' + vb + ' 120'); };
  // off screen: the drift pauses (class is-off); the picture stays
  const seen = new IntersectionObserver((ens) => ens.forEach((en) => en.target.classList.toggle('is-off', !en.isIntersecting)), { rootMargin: '80px 0px' });
  const ribbon = (el) => {
    if (el.classList.contains('is-built')) return;
    const id = 'ceRib' + (++uid);
    const svg = D.createElementNS(NS, 'svg');
    svg.setAttribute('class', 'ce-rib');
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const defs = D.createElementNS(NS, 'defs'), grad = D.createElementNS(NS, 'linearGradient');
    grad.setAttribute('id', id); grad.setAttribute('x1', '0'); grad.setAttribute('y1', '0'); grad.setAttribute('x2', '0'); grad.setAttribute('y2', '1');
    [stop('0', 'ce-rib__s1'), stop('.45', 'ce-rib__s2'), stop('1', 'ce-rib__s3')].forEach((s) => grad.appendChild(s));
    defs.appendChild(grad); svg.appendChild(defs);
    const main = path(wave(70, 12, 1.6, 1440, 'fill'), 'ce-rib__main');
    main.setAttribute('fill', 'url(#' + id + ')');
    svg.appendChild(group('ce-rib__a', [path(wave(58, 9, 0.4, 1440, 'fill'), 'ce-rib__back')]));
    svg.appendChild(group('ce-rib__b', [main, path(wave(70, 12, 1.6, 1440), 'ce-rib__line'), path(wave(79, 10, 1.9, 1440), 'ce-rib__line2')]));
    svg.appendChild(group('ce-rib__c', [path(wave(103, 7, 2.6, 720, 'fill'), 'ce-rib__to')]));
    fit(svg);
    el.appendChild(svg);
    el.setAttribute('aria-hidden', 'true');
    el.classList.add('is-built');
    ribbons.add(svg);
    seen.observe(el);
  };
  let rz = 0;
  W.addEventListener('resize', () => { clearTimeout(rz); rz = setTimeout(() => ribbons.forEach((s) => (s.isConnected ? fit(s) : ribbons.delete(s))), 120); });

  /* ---------- caustics (WebGL) ---------- */
  const TONES = { deep: ['#073D3D', '#22A5AA'], ice: ['#D5EEF1', '#FFFFFF'], cream: ['#F8F5EB', '#FFFFFF'] };
  const rgb = (s, fb) => {
    s = (s || '').trim();
    let m = /^#([\da-f]{6})$/i.exec(s);
    if (m) { const n = parseInt(m[1], 16); return [(n >> 16) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]; }
    m = /^rgba?\(([^)]+)\)/i.exec(s);
    if (m) { const p = m[1].split(/[\s,/]+/).map(Number); return [p[0] / 255, p[1] / 255, p[2] / 255]; }
    return fb ? rgb(fb) : [0, 0, 0];
  };
  const VS = 'attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}';
  const FS = 'precision mediump float;uniform float t;uniform vec2 r;uniform vec3 bg;uniform vec3 li;uniform float sc;' +
    'void main(){vec2 uv=gl_FragCoord.xy/r.y*sc;vec2 p=mod(uv*6.28318,6.28318)-250.;vec2 i=p;float c=1.;float inten=.005;' +
    'for(int n=0;n<4;n++){float tt=t*(1.-(3.5/float(n+1)));i=p+vec2(cos(tt-i.x)+sin(tt+i.y),sin(tt-i.y)+cos(tt+i.x));' +
    'c+=1./length(vec2(p.x/(sin(i.x+tt)/inten),p.y/(cos(i.y+tt)/inten)));}' +
    'c/=4.;c=1.17-pow(c,1.4);float v=pow(abs(c),8.);float fade=smoothstep(0.,1.,gl_FragCoord.y/r.y);' +
    'vec3 col=mix(bg,li,clamp(v*(.35+.65*fade),0.,1.));gl_FragColor=vec4(col,1.);}';
  const lights = new Set(), byEl = new WeakMap();
  const vis = new IntersectionObserver((ens) => ens.forEach((en) => { const o = byEl.get(en.target); if (o && o.gl) { o.on = en.isIntersecting; o.sync(); } }), { rootMargin: '60px 0px' });
  const sized = new ResizeObserver((ens) => ens.forEach((en) => { const o = byEl.get(en.target); if (o && o.gl) { clearTimeout(o.rz); o.rz = setTimeout(() => o.fit(), 100); } }));
  class Caustics {
    constructor(el) {
      this.el = el; this.on = false; this.raf = 0; this.last = 0; this.gl = null; this.t0 = performance.now() - Math.random() * 20000;
      el.setAttribute('aria-hidden', 'true');
      const cv = this.cv = D.createElement('canvas');
      cv.className = 'ce-caustics__cv';
      let gl = null;
      try { gl = cv.getContext('webgl', { antialias: false, alpha: false, premultipliedAlpha: false, powerPreference: 'low-power' }); } catch (e) { gl = null; }
      if (!gl) { el.classList.add('is-fallback'); return; }
      const sh = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); return s; };
      const pr = gl.createProgram();
      gl.attachShader(pr, sh(gl.VERTEX_SHADER, VS)); gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, FS)); gl.linkProgram(pr);
      if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) { el.classList.add('is-fallback'); return; }
      gl.useProgram(pr);
      const buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(pr, 'a'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      this.u = { t: gl.getUniformLocation(pr, 't'), r: gl.getUniformLocation(pr, 'r'), bg: gl.getUniformLocation(pr, 'bg'), li: gl.getUniformLocation(pr, 'li'), sc: gl.getUniformLocation(pr, 'sc') };
      this.gl = gl;
      el.appendChild(cv);
      this.colours();
      this.fit();
      el.classList.add('is-live');
      lights.add(this);
      vis.observe(el);
      sized.observe(el);
    }
    colours() {
      const cs = getComputedStyle(this.el), tone = TONES[this.el.dataset.ceTone] || TONES.deep;
      this.bg = rgb(cs.getPropertyValue('--ce-caustics-bg'), tone[0]);
      this.li = rgb(cs.getPropertyValue('--ce-caustics-light'), tone[1]);
      this.sc = parseFloat(this.el.dataset.ceScale) || 1.15;
    }
    fit() {
      const s = .5, w = Math.max(2, Math.round(this.el.clientWidth * s)), h = Math.max(2, Math.round(this.el.clientHeight * s));
      if (this.cv.width !== w || this.cv.height !== h) { this.cv.width = w; this.cv.height = h; this.gl.viewport(0, 0, w, h); }
      this.draw(performance.now());
    }
    draw(now) {
      const gl = this.gl, u = this.u;
      gl.uniform1f(u.t, 12 + (now - this.t0) / 1000 * .32); gl.uniform2f(u.r, this.cv.width, this.cv.height);
      gl.uniform3fv(u.bg, this.bg); gl.uniform3fv(u.li, this.li); gl.uniform1f(u.sc, this.sc);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
    sync() {
      const run = this.on && !D.hidden && !reduce.matches && this.el.isConnected;
      if (run && !this.raf) {
        const loop = (now) => {
          this.raf = requestAnimationFrame(loop);
          if (now - this.last < 33) return;      // ~30 fps is plenty for slow water light
          this.last = now; this.draw(now);
        };
        this.raf = requestAnimationFrame(loop);
      } else if (!run && this.raf) { cancelAnimationFrame(this.raf); this.raf = 0; }
    }
    destroy() { this.on = false; this.sync(); vis.unobserve(this.el); sized.unobserve(this.el); lights.delete(this); }
  }
  D.addEventListener('visibilitychange', () => lights.forEach((o) => o.sync()));
  if (reduce.addEventListener) reduce.addEventListener('change', () => lights.forEach((o) => { o.sync(); o.draw(performance.now()); }));

  /* ---------- reveal ---------- */
  const show = new IntersectionObserver((ens) => ens.forEach((en) => {
    if (!en.isIntersecting) return;
    en.target.classList.add('is-in');
    show.unobserve(en.target);
  }), { rootMargin: '0px 0px -6% 0px' });
  const reveal = (els) => {
    if (!els.length) return;
    if (reduce.matches) { els.forEach((n) => n.classList.add('is-in', 'is-now')); return; }
    const vh = W.innerHeight, tops = els.map((n) => n.getBoundingClientRect().top);   // every read, then every write
    els.forEach((n, k) => {
      if (tops[k] < vh) { n.classList.add('is-in', 'is-now'); return; }
      if (n.hasAttribute('data-ce-stagger')) Array.prototype.forEach.call(n.children, (c, j) => c.style.setProperty('--ce-ri', Math.min(j, 8)));
      n.classList.add('ce-fx-armed');
      show.observe(n);
    });
  };

  const api = W.CEWater = {
    init(root) {
      root = root || D;
      each(root, '[data-ce-fx="ribbon"]', ribbon);
      each(root, '[data-ce-fx="caustics"]', (el) => { if (!byEl.has(el)) byEl.set(el, new Caustics(el)); });
      const rv = [];
      each(root, '[data-ce-fx="reveal"]:not(.is-in):not(.ce-fx-armed)', (n) => rv.push(n));
      reveal(rv);
    },
    get: (el) => byEl.get(el)
  };
  D.addEventListener('shopify:section:load', (e) => api.init(e.target));
  D.addEventListener('shopify:section:unload', (e) => lights.forEach((o) => e.target.contains(o.el) && o.destroy()));
  const boot = () => { api.init(); H.classList.add('ce-water-ready'); };
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', boot); else boot();
})();

/* ==========================================================================
   MICRO-INTERACTIONS, site-wide (stage 3 wow builder, 09-10-2026). ADDITIVE: the effects above are untouched.
   (Source of this block: tools/wow_fx_micro.js, appended by tools/wow_fx_append.py; edit there and re-run.)
   The looks are ce-fx.css section 11. Everything here is off under reduced motion, never blocks a click and never
   moves the layout. One IntersectionObserver for the glints and one for the eyebrow marks.

     GLINT   .ce-steel / .ce-steel--mist / .ce-steel--deep / .cehm-brands__strip / [data-ce-glint]: the first time
             one is a third in view, a light sweeps across it once (a .ce-steel--sheen surface plays its own pass).
             Only a positioned surface gets the sweep (nothing is ever re-positioned).
     ADDED   the theme's own cart events: "ce:cart:added" (a card's quick add, ce-main.js), "ce:cart:updated"
             (any cart change, ours or Dawn's: the item count went UP) and Dawn's pubsub cartUpdate (the variant).
             Drawer open -> the new line flashes; no drawer -> a burst out of the header's cart icon.
             No cart or product file is changed: it only listens and adds a class for a moment.
     MARK    .ce-eyebrow below the first screen: its 45-degree mark draws in as it arrives.

   window.CEMicro.glint(el)   play the glint on any positioned element now
   window.CEMicro.added()     play the "added" flourish now (for a test)
   ========================================================================== */
(() => {
  'use strict';
  const W = window, D = document;
  if (W.CEMicro || !('IntersectionObserver' in W)) return;
  const reduce = W.matchMedia('(prefers-reduced-motion: reduce)');
  const GLINT = '.ce-steel, .ce-steel--mist, .ce-steel--deep, .cehm-brands__strip, [data-ce-glint]';
  const done = new WeakSet();

  /* ---------- GLINT ----------
     The light is a span inside the surface. The spans are made in idle time after the page has loaded, never while
     it scrolls: this theme has body:has(...) rules, so an element inserted later restyles the whole page (a jank).
     When a surface arrives, the glint is only a class. */
  const idle = W.requestIdleCallback ? (f) => W.requestIdleCallback(f, { timeout: 2500 }) : (f) => setTimeout(f, 400);
  const prep = (el) => {
    if (el.ceGlint !== undefined) return el.ceGlint;
    el.ceGlint = null;
    if (el.classList.contains('ce-steel--sheen') || getComputedStyle(el).position === 'static') return null;
    const g = D.createElement('span');
    g.className = 'ce-fx-glint';
    g.setAttribute('aria-hidden', 'true');
    el.appendChild(g);
    el.ceGlint = g;
    return g;
  };
  const glint = (el) => {
    if (!el || reduce.matches || D.hidden) return;
    if (el.classList.contains('ce-steel--sheen')) {
      el.classList.add('is-lit');
      setTimeout(() => el.classList.remove('is-lit'), 1900);
      return;
    }
    const g = prep(el);
    if (!g) return;
    g.classList.remove('is-run');
    void g.offsetWidth;
    g.classList.add('is-run');
  };
  const gio = new IntersectionObserver((ens) => ens.forEach((en) => {
    if (!en.isIntersecting || en.boundingClientRect.width < 160) return;
    gio.unobserve(en.target);
    glint(en.target);
  }), { threshold: 0.34 });

  /* ---------- MARK ---------- */
  const mio = new IntersectionObserver((ens) => ens.forEach((en) => {
    if (!en.isIntersecting) return;
    mio.unobserve(en.target);
    en.target.classList.remove('ce-fx-eb-wait');
    en.target.classList.add('ce-fx-eb-in');
    setTimeout(() => en.target.classList.remove('ce-fx-eb-in'), 900);
  }), { rootMargin: '0px 0px -8% 0px' });

  const scan = (root) => {
    root = root || D;
    const pick = (sel) => (root.matches && root.matches(sel) ? [root] : []).concat(Array.prototype.slice.call(root.querySelectorAll(sel)));
    const hosts = pick(GLINT).filter((el) => !done.has(el));
    hosts.forEach((el) => done.add(el));
    if (hosts.length && !reduce.matches) {
      // every span in one go, in idle time; only then start watching (a surface already on screen plays then)
      idle(() => { hosts.forEach(prep); hosts.forEach((el) => gio.observe(el)); });
    }
    if (reduce.matches) return;
    // eyebrows: every read first, then every write (no layout work in a loop)
    const ebs = pick('.ce-eyebrow').filter((el) => !done.has(el));
    if (!ebs.length) return;
    const vh = W.innerHeight;
    const tops = ebs.map((el) => el.getBoundingClientRect());
    ebs.forEach((el, i) => {
      done.add(el);
      const r = tops[i];
      if (!r.width || r.top < vh) return;            // hidden, or already on the first screen: left alone (no blink)
      el.classList.add('ce-fx-eb-wait');
      mio.observe(el);
    });
  };

  /* ---------- ADDED ---------- */
  const countNow = () => {
    const n = D.querySelector('#cart-icon-bubble .cart-count-bubble [aria-hidden]') || D.querySelector('[data-ce-cart-count]');
    const v = n ? parseInt(n.textContent, 10) : 0;
    return isNaN(v) ? 0 : v;
  };
  let last = null;            // item count last seen
  let variant = '';           // the variant last added (when an event told us)
  let shownAt = 0;
  const burst = () => {
    const icon = D.getElementById('cart-icon-bubble');
    if (!icon) return;
    const r = icon.getBoundingClientRect();
    if (!r.width || r.bottom < 0 || r.top > W.innerHeight) return;
    const b = D.createElement('span');
    b.className = 'ce-fx-burst';
    b.setAttribute('aria-hidden', 'true');
    b.style.left = Math.round(r.left + r.width / 2) + 'px';
    b.style.top = Math.round(r.top + Math.min(r.height / 2, 22)) + 'px';
    D.body.appendChild(b);
    setTimeout(() => b.remove(), 1000);
  };
  const flash = (drawer) => {
    let line = null;
    if (variant) {
      const q = drawer.querySelector('[data-quantity-variant-id="' + variant + '"], a[href*="variant=' + variant + '"]');
      line = q && q.closest('.cart-item, li, tr');
    }
    if (!line) line = drawer.querySelector('.cart-item');
    if (!line) return false;
    line.classList.remove('ce-fx-just-added');
    void line.offsetWidth;
    line.classList.add('ce-fx-just-added');
    setTimeout(() => line.classList.remove('ce-fx-just-added'), 2000);
    return true;
  };
  const added = () => {
    if (reduce.matches) return;
    const now = Date.now();
    if (now - shownAt < 900) return;                 // the same add, heard twice (our event + Dawn's)
    shownAt = now;
    // give Dawn a moment to draw and open the drawer
    setTimeout(() => {
      const drawer = D.querySelector('cart-drawer.active, cart-drawer.is-open');
      if (!(drawer && flash(drawer))) burst();
      variant = '';
    }, 160);
  };
  D.addEventListener('ce:cart:added', (e) => {
    const it = e.detail && e.detail.item;
    if (it) variant = String(it.variant_id || it.id || '');
  });
  D.addEventListener('ce:cart:updated', (e) => {
    const c = e.detail && e.detail.cart;
    if (!c || typeof c.item_count !== 'number') return;
    if (last !== null && c.item_count > last) added();
    last = c.item_count;
  });
  try {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (ev) => { if (ev && ev.productVariantId) variant = String(ev.productVariantId); });
    }
  } catch (e) { /* Dawn's pubsub is not on this page: the count still tells */ }

  const boot = () => { if (last === null) last = countNow(); scan(D); };
  D.addEventListener('shopify:section:load', (e) => scan(e.target));
  W.CEMicro = { glint, added, scan };
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', boot); else boot();
})();
